import { useEffect, useState } from "react";
import { Plus, Paperclip, Send, Pencil, Trash2 } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { PlanificacionForm, ESTADOS_PLAN } from "@/components/PlanificacionForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function DocentePlanificacion() {
  const { config } = useAuth();
  const [planes, setPlanes] = useState(null);
  const [materias, setMaterias] = useState([]);
  const [form, setForm] = useState(null);

  const cargar = () => api.get("/planificaciones").then((r) => setPlanes(r.data)).catch(() => setPlanes([]));

  useEffect(() => {
    cargar();
    api.get("/docente/materias").then((r) => setMaterias(r.data)).catch(() => {});
  }, []);

  const guardar = async (datos) => {
    try {
      if (datos.id) await api.put(`/planificaciones/${datos.id}`, datos);
      else await api.post("/planificaciones", datos);
      toast.success(datos.enviar ? "Planificación enviada a coordinación" : "Borrador guardado");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const enviar = async (p) => {
    try {
      await api.post(`/planificaciones/${p.id}/enviar`);
      toast.success("Planificación enviada a coordinación");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (p) => {
    try {
      await api.delete(`/planificaciones/${p.id}`);
      toast.success("Borrador eliminado");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!planes) return <Loading />;

  return (
    <div data-testid="docente-planificacion">
      <PageTitle title="Mi planificación"
                 subtitle="Registre su planificación por materia y periodo, envíela a coordinación y consulte sus observaciones.">
        <Button className="rounded-full" onClick={() => setForm({})} data-testid="nueva-planificacion"
                disabled={materias.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> Nueva planificación
        </Button>
      </PageTitle>

      {planes.length === 0 ? (
        <Empty testId="planes-vacios">
          Aún no ha registrado planificaciones. Cree una y envíela a coordinación para su revisión.
        </Empty>
      ) : (
        <div className="space-y-3" data-testid="lista-mis-planes">
          {planes.map((p) => (
            <article key={p.id} className="rounded-lg border border-border/60 bg-card p-5" data-testid={`plan-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-base font-bold">{p.titulo}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.materia_nombre} · {p.grado} · {p.periodo}{p.semana ? ` · ${p.semana}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {p.puntuacion != null && (
                    <Badge variant="outline" className="font-mono text-[10px]">{p.puntuacion}/100</Badge>
                  )}
                  <Badge variant={ESTADOS_PLAN[p.estado]?.variant}>{ESTADOS_PLAN[p.estado]?.label}</Badge>
                </div>
              </div>

              {p.comentario_revision && (
                <div className="mt-4 rounded-md border border-border bg-muted/40 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Observaciones de coordinación
                  </p>
                  <p className="mt-1 text-sm">{p.comentario_revision}</p>
                  {p.revisado_por && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {p.revisado_por} · {String(p.revisado_en).slice(0, 10)}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {p.archivo_nombre && (
                  <a href={`${API}/planificaciones/${p.id}/archivo`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                    <Paperclip className="h-3 w-3" /> {p.archivo_nombre}
                  </a>
                )}
                {["borrador", "correcciones", "rechazada"].includes(p.estado) && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForm(p)}
                            data-testid={`editar-plan-${p.id}`}>
                      <Pencil className="mr-1 h-3 w-3" /> Editar
                    </Button>
                    <Button size="sm" className="rounded-full" onClick={() => enviar(p)}
                            data-testid={`enviar-plan-${p.id}`}>
                      <Send className="mr-1 h-3 w-3" /> Enviar a coordinación
                    </Button>
                  </>
                )}
                {p.estado === "borrador" && (
                  <Button size="sm" variant="ghost" onClick={() => eliminar(p)} data-testid={`eliminar-plan-${p.id}`}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="modal-plan-form">
          <DialogHeader>
            <DialogTitle className="font-display">
              {form?.id ? "Editar planificación" : "Nueva planificación"}
            </DialogTitle>
            <DialogDescription>
              Guarde un borrador para seguir trabajando o envíela directamente a coordinación.
            </DialogDescription>
          </DialogHeader>
          <PlanificacionForm materias={materias} docentes={[]} periodos={config.periodos}
                             valor={form?.id ? form : null}
                             onGuardar={(datos) => guardar({ ...datos, id: form?.id })} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
